import { useSearchBlockedUsersQuery, useExportBlockedUsersLazyQuery } from '@exam-center/graphql';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Loading,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { triggerFileDownload } from '@shared/utils';
import { Fragment } from 'react';

import { BLOCKED_TYPE_LABEL_MAP } from '../../constants';
import { useAdminBlockedUsersListSearchParams } from '../../hooks';

interface AdminExamCourseEnablementModalProps {
  open: boolean;
  onClose: () => void;
}

interface ContentItem {
  label: string;
  value: string;
}

export default function AdminExamCourseEnablementModal(
  props: Readonly<AdminExamCourseEnablementModalProps>,
) {
  const { open: isOpen, onClose } = props;
  const { showSnackbar } = useSnackbar();

  const [{ searchBlockedUsersInput, search, blockedTypes, examCenters }] =
    useAdminBlockedUsersListSearchParams();

  const { data: blockedUsersData, loading: isSearchingBlockedUsers } = useSearchBlockedUsersQuery({
    variables: searchBlockedUsersInput,
  });

  const isBlockedUsersEmpty = !blockedUsersData?.ecSearchBlockedUsers?.total;

  const [exportBlockedUsers, { loading: isExporting }] = useExportBlockedUsersLazyQuery();

  const handleDownload = async () => {
    await exportBlockedUsers({
      variables: {
        exportBlockedUsersInput: {
          filter: searchBlockedUsersInput.searchBlockedUsersInput.filter,
          examCenterCodes: searchBlockedUsersInput.searchBlockedUsersInput.examCenterCodes,
          blockedTypes: searchBlockedUsersInput.searchBlockedUsersInput.blockedTypes,
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const signedUrl = data.ecExportBlockedUsers.signedUrl;
        if (!signedUrl) {
          showSnackbar({
            message: 'ส่งอออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
          onClose();
          return;
        }

        triggerFileDownload(signedUrl);
        showSnackbar({
          message: 'ส่งออกข้อมูลเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
        onClose();
      },
    });
  };

  const content: ContentItem[] = [
    {
      label: 'ผลการค้นหา:',
      value: search || '-',
    },
    {
      label: 'ประเภท:',
      value: blockedTypes.length
        ? blockedTypes.map((blockType) => BLOCKED_TYPE_LABEL_MAP[blockType]).join(', ')
        : 'ทั้งหมด',
    },
    {
      label: 'ศูนย์ทดสอบ:',
      value: examCenters.length ? examCenters.join(', ') : 'ทั้งหมด',
    },
  ];

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='xs'>
      <DialogTitle onClose={onClose}>ยืนยันการส่งออกข้อมูล</DialogTitle>
      <Divider />
      <DialogContent>
        {isBlockedUsersEmpty ? (
          <Alert severity='error' sx={{ mb: 3 }}>
            ไม่สามารถดาวน์โหลดได้ เนื่องจากไม่มีข้อมูลที่ตรงกับเงื่อนไข
          </Alert>
        ) : null}
        {isSearchingBlockedUsers ? <Loading /> : <ContentItem content={content} />}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
          ยกเลิก
        </Button>
        <Button
          size='large'
          onClick={handleDownload}
          loading={isExporting}
          disabled={isBlockedUsersEmpty || isSearchingBlockedUsers}
        >
          ดาวน์โหลด
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ContentItem(props: Readonly<{ content: ContentItem[] }>) {
  return (
    <>
      <Text variant='body1Semibold'>สรุปข้อมูลที่ต้องการ</Text>
      <Grid
        sx={(theme) => ({
          pt: theme.spacing(3),
          rowGap: theme.spacing(3),
          columnGap: theme.spacing(4),
          gridTemplateColumns: '100px 1fr',
        })}
      >
        {props.content.map(({ label, value }) => (
          <Fragment key={label}>
            <Text variant='body1Semibold'>{label}</Text>
            {typeof value === 'string' ? <Text>{value}</Text> : value}
          </Fragment>
        ))}
      </Grid>
    </>
  );
}
